import { useCallback, useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  LiveKitRoom,
  RoomAudioRenderer,
  useVoiceAssistant,
  BarVisualizer,
  VoiceAssistantControlBar,
  useRoomContext,
  useConnectionState,
  useChat,
} from '@livekit/components-react';
import { ConnectionState } from 'livekit-client';
import '@livekit/components-styles';
import { LiveKitConnectionDetails, dispatchAgent } from '../lib/livekit';

interface ConversationContentProps {
  onDisconnect: () => void;
  roomName: string;
}

function ConversationContent({ onDisconnect, roomName }: ConversationContentProps) {
  const { state, audioTrack } = useVoiceAssistant();
  const room = useRoomContext();
  const connectionState = useConnectionState();
  const { chatMessages } = useChat();
  const dispatchAttempted = useRef(false);

  // Dispatch agent when connected
  useEffect(() => {
    if (connectionState === ConnectionState.Connected && !dispatchAttempted.current) {
      dispatchAttempted.current = true;
      console.log('Room connected, dispatching agent to room:', roomName);

      dispatchAgent(roomName)
        .then((result) => {
          console.log('Agent dispatched successfully:', result);
        })
        .catch((err) => {
          console.error('Failed to dispatch agent:', err);
          // Still allow the conversation to continue even if dispatch fails
          // The agent might already be listening or there's an auto-dispatch fallback
        });
    }
  }, [connectionState, roomName]);

  // Handle disconnect
  const handleDisconnect = useCallback(() => {
    room.disconnect();
    onDisconnect();
  }, [room, onDisconnect]);

  // Get agent state display text
  const getStateText = () => {
    switch (state) {
      case 'listening':
        return 'Listening...';
      case 'thinking':
        return 'Thinking...';
      case 'speaking':
        return 'Speaking...';
      default:
        return 'Connecting...';
    }
  };

  return (
    <div className="conversation-content">
      <header className="conversation-header">
        <h1>Dictador VA</h1>
        <div className="connection-status">
          <span
            className={`status-indicator ${connectionState === ConnectionState.Connected ? 'connected' : ''}`}
          />
          <span>
            {connectionState === ConnectionState.Connected ? 'Connected' : 'Connecting...'}
          </span>
        </div>
      </header>

      <main className="conversation-main">
        {/* Audio Visualizer */}
        <div className="visualizer-container">
          <BarVisualizer
            state={state}
            barCount={7}
            trackRef={audioTrack}
            className="audio-visualizer"
          />
          <p className="agent-state">{getStateText()}</p>
        </div>

        {/* Transcript */}
        <div className="transcript-container">
          <h3>Conversation</h3>
          <div className="transcript-messages">
            {chatMessages.length === 0 ? (
              <p className="transcript-empty">Start speaking to begin the conversation...</p>
            ) : (
              chatMessages.map((msg) => (
                <div
                  key={msg.id}
                  className={`transcript-message ${msg.from?.isLocal ? 'local' : 'remote'}`}
                >
                  <span className="message-sender">{msg.from?.isLocal ? 'You' : 'Assistant'}</span>
                  <span className="message-text">{msg.message}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </main>

      <footer className="conversation-footer">
        <VoiceAssistantControlBar />
        <button onClick={handleDisconnect} className="end-call-button">
          End Conversation
        </button>
      </footer>

      {/* Audio renderer for the agent's audio */}
      <RoomAudioRenderer />
    </div>
  );
}

export function ConversationPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [connectionDetails, setConnectionDetails] = useState<LiveKitConnectionDetails | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const details = location.state?.connectionDetails as LiveKitConnectionDetails | undefined;

    if (!details) {
      // No connection details, redirect to dashboard
      navigate('/dashboard', { replace: true });
      return;
    }

    setConnectionDetails(details);
  }, [location.state, navigate]);

  const handleDisconnect = useCallback(() => {
    navigate('/dashboard', { replace: true });
  }, [navigate]);

  const handleError = useCallback((err: Error) => {
    console.error('LiveKit connection error:', err);
    setError(err.message);
  }, []);

  if (error) {
    return (
      <div className="conversation-error">
        <h2>Connection Error</h2>
        <p>{error}</p>
        <button onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
      </div>
    );
  }

  if (!connectionDetails) {
    return (
      <div className="conversation-loading">
        <div className="loading-spinner" />
        <p>Preparing conversation...</p>
      </div>
    );
  }

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <LiveKitRoom
      token={connectionDetails.token}
      serverUrl={connectionDetails.url}
      connect={true}
      audio={true}
      video={false}
      onError={handleError}
      onDisconnected={handleDisconnect}
      className="conversation-room"
    >
      <ConversationContent onDisconnect={handleDisconnect} roomName={connectionDetails.roomName} />
    </LiveKitRoom>
  );
}
